import React from "react";
import "./RecommendationsSection.scss";
import ContentSection from "../components/ContentSection";
import SectionHeader from "../components/SectionHeader";
import TwoColumnLayout from "../components/TwoColumnLayout";

const RecommendationsSection: React.FC = () => {
  const leftContent = (
    <div className="recommendations-section-content">
      <p>
        For the reentry workforce field, 2025 has been a year defined by both
        disruption and determination. Federal funding instability in delayed
        contracts, sudden cancellations, and shifting policy priorities have
        directly and indirectly impacted thousands of justice-involved job
        seekers, their families, and the organizations that serve them. Yet,
        amid these challenges, the field has demonstrated remarkable
        adaptability and resolve. 
      </p>
      <p>
        As we look forward, the path ahead will require not only resilience, but
        also intentional strategies to mitigate the impact of uncertainty. Based
        on insights from the field and our own expertise, we recommend the
        following:
      </p>
    </div>
  );

  const rightContent = <></>;

  return (
    <ContentSection className="recommendations-section">
      <SectionHeader
        title="Recommendations and Conclusion"
        description="Looking Ahead - Building Resilience in Uncertain Times"
      />
      <TwoColumnLayout leftContent={leftContent} rightContent={rightContent} />

      <h3 className="sr-only">Recommendations</h3>
      <ol className="takeaways-grid">
        <li className="takeaway-item">
          <strong>Prioritize diversification of funding streams.</strong>
          <p>
            The instability of federal contracts has highlighted the risks of
            depending too heavily on a single source of support. To build
            long-term stability, organizations should develop balanced revenue
            strategies, expanding individual donor bases, cultivating corporate
            partnerships, engaging philanthropy, and exploring earned-income
            opportunities. While nothing can replace the investment from the
            federal government, organizations should diversify with a broader
            mix of resources to create greater resilience in the face of
            uncertainty.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>
            Cultivate financial resilience, and share resources when possible.
          </strong>
          <p>
            Strong reserves, lean operations, and strategic partnerships are
            essential buffers in unpredictable times. Building financial
            resilience can also include strengthening the field collectively.
            Where possible, sharing back-office services, facilities, or even
            staff expertise can reduce costs and increase impact across
            networks.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>
            Invest in building ecosystems and cross-sector collaboration, don’t
            revert to operating in silos. 
          </strong>
          <p>
            When resources shrink, the temptation is to turn inward. Yet, now
            more than ever, collaboration across workforce, housing, behavioral
            health, education, and employer systems is the key to
            sustainability. Building interconnected ecosystems allows
            organizations to expand reach and program offerings and create
            stronger support systems for job seekers.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>Clarify your “why” and guard against mission drift.</strong>
          <p>
            In a climate of uncertainty, opportunities outside of an
            organization’s core mission can be both tempting and distracting.
            Organizations must continually ground themselves in their core
            mission, revisiting values and priorities to avoid drifting away
            from their central purpose. Staying focused on the “why” ensures
            long-term strength.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>
            Articulate a long-term strategy and communicate it with
            philanthropic partners.
          </strong>
          <p>
            Funders want to invest in organizations with a plan that
            acknowledges risks, outlines mitigation strategies, and demonstrates
            staying power.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>Upskill staff to keep up with technological advances.</strong>
          <p>
            Technology is reshaping every sector, including reentry workforce
            development. Staff must be equipped with the skills to integrate
            digital tools, leverage data, and navigate emerging technologies
            such as AI. Investing in ongoing professional development ensures
            that organizations stay ahead of change rather than being left
            behind.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>
            Be intentional about framing impact and communicating value.
          </strong>
          <p>
            In an era where narratives shape funding and policy, organizations
            must sharpen how they articulate their value. Data and stories must
            work together to demonstrate measurable outcomes and long-term
            impact. Clear, intentional communication ensures that the field’s
            contributions are understood, supported, and sustained.
          </p>
        </li>
      </ol>
    </ContentSection>
  );
};

export default RecommendationsSection;
